<template>
    <footer
        :class="$style.footer"
        :style="!jobsForApproval.length && 'margin-top: 80px'"
        class="pt-5 pb-5"
        data-xpath="app-footer"
    >
        <div class="container p-0 d-flex" data-xpath="footer-container">
            <div class="d-flex align-items-start mr-auto" data-xpath="footer-main-section">
                <div class="pr-5 mr-5" style="width: 200px;" data-xpath="footer-logo-container">
                    <img src="../../assets/logo.svg" width="136" height="33" alt="Logo" data-xpath="footer-logo" />
                </div>
                <div class="footer-apps" data-xpath="footer-apps">
                    <div :class="$style.columnHeader" class="mb-2 text-uppercase" data-xpath="footer-get-app">Get the App</div>
                    <a 
                        href="https://play.google.com/" 
                        target="_blank" 
                        class="mr-3 mb-2"
                        data-xpath="footer-google-play"
                    >
                        <img 
                            src="../../assets/google-play-badge.svg" 
                            width="135" 
                            height="40" 
                            alt="Google Play"
                            data-xpath="footer-google-play-img"
                        />
                    </a>
                    <a 
                        href="https://www.apple.com/ios/app-store/" 
                        target="_blank"
                        data-xpath="footer-app-store"
                    >
                        <img 
                            src="../../assets/app-store.svg" 
                            width="135" 
                            height="40" 
                            alt="App Store"
                            data-xpath="footer-app-store-img"
                        />
                    </a>
                </div>
            </div>

            <div class="d-flex align-items-start" data-xpath="footer-links-section">
                <div class="mr-5" data-xpath="footer-site-links">
                    <div :class="$style.columnHeader" class="text-uppercase" data-xpath="footer-site-header">Site</div>
                    <div :class="$style.items" data-xpath="footer-shifts-link">Shifts Lists</div>
                    <div :class="$style.items" data-xpath="footer-staff-link">Staff Management</div>
                    <div :class="$style.items" data-xpath="footer-about-link">About</div>
                    <div :class="$style.items" data-xpath="footer-contact-link">Contact</div>
                </div>
                <div class="mr-5 ml-4" data-xpath="footer-legal-links">
                    <div :class="$style.columnHeader" class="text-uppercase" data-xpath="footer-legal-header">Legal</div>
                    <div :class="$style.items" data-xpath="footer-terms-link">Terms & Conditions</div>
                    <div :class="$style.items" data-xpath="footer-privacy-link">Privacy Policy</div>
                    <div :class="$style.items" data-xpath="footer-security-link">Security</div>
                </div>
                <div class="ml-4" data-xpath="footer-social-links">
                    <div :class="$style.columnHeader" class="text-uppercase" data-xpath="footer-social-header">Social</div>
                    <a 
                        href="https://www.youtube.com/" 
                        class="mr-1" 
                        target="_blank"
                        data-xpath="footer-youtube-link"
                    >
                        <img 
                            src="../../assets/youtube.svg" 
                            width="31px" 
                            height="21px"
                            alt="YouTube"
                            data-xpath="footer-youtube-img"
                        />
                    </a>
                    <a 
                        href="https://www.facebook.com/" 
                        class="ml-3 mr-3" 
                        target="_blank"
                        data-xpath="footer-facebook-link"
                    >
                        <img 
                            src="../../assets/facebook.svg" 
                            width="11px" 
                            height="23px"
                            alt="Facebook"
                            data-xpath="footer-facebook-img"
                        />
                    </a>
                    <a 
                        href="https://twitter.com/" 
                        class="mr-3" 
                        target="_blank"
                        data-xpath="footer-twitter-link"
                    >
                        <img 
                            src="../../assets/twitter.svg" 
                            width="26" 
                            height="21"
                            alt="Twitter"
                            data-xpath="footer-twitter-img"
                        />
                    </a>
                    <a 
                        href="https://www.linkedin.com/" 
                        class="mr-2" 
                        target="_blank"
                        data-xpath="footer-linkedin-link"
                    >
                        <img 
                            src="../../assets/linkedin.svg" 
                            width="23" 
                            height="23"
                            alt="LinkedIn"
                            data-xpath="footer-linkedin-img"
                        />
                    </a>
                </div>
            </div>
        </div>
        <br/>
        <h6 
            class="webVersion" 
            v-if="appVersion && (appVersion.backendVersion != undefined && appVersion.frontendVersion != undefined)"
            data-xpath="footer-version"
        >
            {{appVersion.frontendVersion + ' . ' + appVersion.backendVersion}}
        </h6>
    </footer>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';

// Initialize the store
const store = useStore();

// Initialize reactive state
const appVersion = ref('');

// Get state from store
const jobsForApproval = computed(() => store.state.jobsForApproval);
const currentUserRole = computed(() => store.state.currentUserRole);

// Methods
async function fetchAppVersion() {
    appVersion.value = await store.dispatch('getAppVersion');
}

// Lifecycle hooks
onMounted(async () => {
    await fetchAppVersion();
});

// Uncomment if you need to watch changes to currentUserRole
// watch(currentUserRole, async () => {
//     await fetchAppVersion();
// });
</script>


<style lang="scss" module>
@import '../../styles/variables.scss';

.footer {
	justify-content: center;
	background-color: $blue;
	border-top: 5px solid $sp-color-light-blue;
	color: white;
	font-size: 14px;
}

.columnHeader {
	margin-bottom: 5px;
	font-size: 11px;
	color: $sp-color-text-light;
}

.items {
	color: $white;
}
</style>

<style lang="scss" scoped>
@media screen and (max-width: 1600px) {
	.container {
		max-width: 1200px;
	}
}

@media screen and (max-width: 1250px) {
	.container {
		max-width: 94vw;
	}

	.footer-apps {
		display: flex;
		flex-direction: column;
	}
}
</style>
<style>
.webVersion{
	margin-bottom: -40px;
	margin-left: 20px;
	font-family: 'Proxima Nova Normal';
	font-weight: 400;
	font-size: 13px;
	line-height: 13px;
	color: rgba(255, 255, 255, 0.4);
}
</style>
